// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartViewer.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Report Part Viewer component
 */

/**
 * This component is intended to show only report part content.
 * Parameters locate in the params property.
 * id: required. Id of report part.
 * containerId: required. Id of report which contents report part.
 * Options from query:
 * - width: option (default is 100%), in pixel.
 * - height: option (default is 100%), in pixel.
 * - hideTitle: option (default is undefined), in truthy value.
 * - separator: option (default is undefined), encoded separator value.
 * - draftId : draftId to get draft report definition
 * - reportPartName : report part tile for print draft
 *
 * Example build URI:
 * var ContentRoutes = require('routes/Izenda.ContentRoutes').contentRoutes;
 * var ROUTE = require('routes/Izenda.ContentRoutes').route;
 * var uri = routeUtils.getViewerUrl(
 *   'ReportPart',
 *   {
 *     id: '00867012-9060-4347-8460-ac41aa3291f2'
 *   }
 * );
 * // result: /viewer/reportpart/00867012-9060-4347-8460-ac41aa3291f2?width=100&height=200
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import subReportLink from 'share/utils/Izenda.SubReportLink';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import ReportPartFrontSideViewer from 'modules/ReportDesigner/Fields/ReportBody/ReportPartFrontSideViewer/Izenda.ReportPartFrontSideViewer';
import uiState from 'logic/Izenda.UiState';
import AppStore from 'logic/Izenda.AppStore';
import { isNull, get } from 'lodash/fp';

@connect()
export default class ReportPartViewer extends Component {
  static propTypes = {
    params: PropTypes.object,
    location: PropTypes.object
  };

  /**
   * Set current user info when printing
   * @param {Boolean} printingMode 
   * @param {Object} query
   */
  setCurrentUserInfoPrinting(printingMode: Boolean, query: Object) {
    if (printingMode) {
      // Set current user to use user date format in case exporting, the page did not set current user before
      if (isNull(uiState.currentUser.userName)) {
        AppStore.accountStore.loadCurrentUser().then((result: any) => {
          result.success && uiState.setCurrentUser(result.data);
        });
      }

      // Set selected tenant
      const tenantId = get('tenantId', query);
      tenantId && uiState.setTenantId(tenantId);
    }
  }

  constructor(props: any) {
    super(props);
    const { printingMode, reportPartId, reportId, queryObject } = props;
    const params = props.params || {};
    const location = props.location || {};
    const query = location.query || queryObject || {};

    this.printingMode = get('print', query) === 'true' || printingMode;
    this.reportPartId = reportPartId || params.id;
    this.reportId = reportId;

    this.setCurrentUserInfoPrinting(this.printingMode, query);

    if (queryObject) {
      this.subReportFilter = queryObject;
    } else if (query) {
      if (query.tempUrl) {
        this.subReportFilter = subReportLink.readSubReportQuery(query);
      } else {
        this.subReportFilterFromQuery = subReportLink.convertQueryToFilter(query);
      }
    }
  }

  setDom = (domNode: any) => {
    this.dom = domNode;
  };

  render() {
    const { props } = this;
    const {
      isFirstReportPart,
      afterRenderCallBack,
      notInvokeRenderCallBackAfterDidMount,
      doesBreakPage,
      finishedRender,
      exportFormatSetting,
      setting,
      hideGridPopup,
      reportPart,
      store
    } = props;
    const location = props.location || {};
    const query = location.query || {};
    const { hideTitle, separator, width, height, pageNumber, elementsCountInContainer } = query;

    const frontSide = (
      <div ref={this.setDom}>
        <ReportPartFrontSideViewer
          reportPart={reportPart}
          store={store}
          forExporting={!!width && !!height}
          width={width}
          height={height}
          reportId={this.reportId}
          reportPartId={this.reportPartId}
          location={location}
          subReportFilter={this.subReportFilter}
          subReportFilterFromQuery={this.subReportFilterFromQuery}
          printingMode={this.printingMode}
          hideGridPopup={hideGridPopup}
          setting={setting}
          exportFormatSetting={exportFormatSetting}
          finishedRender={finishedRender}
          doesBreakPage={doesBreakPage}
          isFirstReportPart={isFirstReportPart}
          afterRenderCallBack={afterRenderCallBack}
          notInvokeRenderCallBackAfterDidMount={notInvokeRenderCallBackAfterDidMount}
          hideTitle={hideTitle}
          separator={separator}
          elementsCountInContainer={elementsCountInContainer}
          pageNumber={pageNumber}
        />
      </div>
    );

    return (
      <div className='u_fullSize izenda-ReportPartViewer'>
        {this.printingMode ? frontSide : <ScrollBox>{frontSide}</ScrollBox>}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/views/Izenda.ReportPartViewer.js